import Button from "@components/ui/button";
import {
  Card,
  CardCloseButton,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import Spinner from "@components/ui/spinner";
import Switch from "@components/ui/switch";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useState } from "react";
import { toast } from "react-toastify";
import { track } from "@/features/analytics";
import {
  useGetCurrentUserQuery,
  useGetUsersQuery,
  useUpdateFolderMutation,
} from "@/shared/services";
import {
  type IStoriesFolder,
  type IStoriesFolderUser,
  StoriesFolderUserPermEnum,
} from "@/shared/types";
import { MembersDialogInput } from "./members-dialog-input";
import { MembersDialogUser } from "./members-dialog-user";

const DIALOG_ID = "stories-lib-folder-members";

interface IProps {
  folder: IStoriesFolder;
}

export const MembersDialog = ({ folder: folderData }: IProps) => {
  const dispatch = useAppDispatch();
  const [selectedUsers, setSelectedUsers] = useState<IStoriesFolderUser[]>([
    ...(folderData.users ?? []),
  ]);

  const { data: currentUser } = useGetCurrentUserQuery();

  const [isShareEveryone, setIsShareEveryone] = useState(
    !!folderData.openAccess,
  );
  const { isFetching: isLoadingUsers } = useGetUsersQuery();
  const { mutateAsync: updateFolder, isPending: isUpdatingFolder } =
    useUpdateFolderMutation();

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handleSubmit = async () => {
    if (isUpdatingFolder) return;

    try {
      await updateFolder({
        id: folderData._id,
        openAccess: isShareEveryone,
        users: isShareEveryone ? [] : selectedUsers,
      });
    } catch (e) {
      track("error updating media folder members", {
        folderId: folderData._id,
        error: e,
      });
      toast.error("Error updating folder members");
    }

    handleClose();
  };

  return (
    <Card className="tw-w-[512px]">
      <CardHeader className="tw-flex tw-items-center tw-justify-between">
        <CardTitle className="tw-flex">
          People with access to "
          <span className="tw-block tw-max-w-36 tw-truncate tw-whitespace-nowrap">
            {folderData.name}
          </span>
          " folder
        </CardTitle>
        <CardCloseButton onClick={handleClose} />
      </CardHeader>
      <CardContent>
        <div className="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-bg-blue-50 tw-px-4 tw-py-3">
          <span className="tw-font-sans tw-text-base tw-font-normal tw-text-blue">
            Share with everyone in your organization
          </span>
          <Switch.Toggle
            size="md"
            checked={isShareEveryone}
            onCheckedChange={(val) => {
              if (val === true) {
                setSelectedUsers([]);
              } else {
                if (currentUser) {
                  setSelectedUsers([
                    {
                      oktaId: currentUser.oktaId,
                      status: StoriesFolderUserPermEnum.Owner,
                    },
                  ]);
                }
              }
              setIsShareEveryone(val);
            }}
          />
        </div>
        <div
          className={cn(
            "tw-mt-4",
            isShareEveryone && "tw-pointer-events-none tw-opacity-50",
          )}
        >
          <MembersDialogInput
            isShareEveryone={isShareEveryone}
            selectedUsers={selectedUsers}
            onSelect={(user) => {
              setSelectedUsers((prev) => [
                ...prev,
                {
                  oktaId: user.oktaId,
                  status: StoriesFolderUserPermEnum.Editor,
                },
              ]);
            }}
          />
        </div>

        {!isShareEveryone && (
          <MembersDialogUser
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
          />
        )}
      </CardContent>
      <CardFooter className="tw-justify-end tw-gap-4">
        <Button
          variant="secondary"
          className="tw-font-sans tw-text-sm tw-font-medium"
          onClick={handleClose}
        >
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoadingUsers}
          className={cn(
            "tw-font-sans tw-text-sm tw-font-medium",
            isUpdatingFolder && "tw-cursor-not-allowed",
          )}
        >
          {isUpdatingFolder ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Saving...
            </>
          ) : (
            "Save changes"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

MembersDialog.id = DIALOG_ID;
